import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {
  COST_AVOIDANCE_DAILY_QUERY_KEY,
  COST_AVOIDANCE_FOR_RANGE_QUERY_KEY,
  COST_AVOIDANCE_MONTHLY_QUERY_KEY,
  ELECTRICITY_QUERY_KEY,
  FEATURE_NAMESPACE,
  OPTIMIZED_SCHEDULES_STATS_QUERY_KEY,
  OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY,
  SCHEDULE_QUERY_KEY,
  STOCK_DEVELOPMENT_QUERY_KEY,
  getAcceptOptimizedPlanPath
} from '../consts'

import {useTrackPlannerEvent} from './useTrackPlannerEvent'

type Variables = {scheduleId: number}

export const useAcceptOptimizedPlan = () => {
  const {plantId: plantCode} = useUpmPlantContext()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()
  const trackPlannerEvent = useTrackPlannerEvent()

  const {mutate, isPending: isLoading} = useMutation<void, AxiosError<ErrorResponse>, Variables>({
    mutationFn: async ({scheduleId}) => {
      await ApiClient.axiosInstance.patch<void>(
        getAcceptOptimizedPlanPath(plantCode, scheduleId),
        {}
      )
    },
    onError: (error) => {
      const detail = error.response?.data.detail
      if (detail) {
        spawnErrorToast(detail)
      } else {
        raiseError(error)
      }
    },
    onSuccess: () => {
      trackPlannerEvent({name: 'onAcceptOptimizedPlan'})
      spawnSuccessToast(t('optimizer.acceptOptimizedPlanNotification'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: [SCHEDULE_QUERY_KEY], refetchType: 'all'})
      void queryClient.invalidateQueries({
        queryKey: [ELECTRICITY_QUERY_KEY, {plantCode}],
        refetchType: 'all'
      })
      void queryClient.removeQueries({queryKey: [STOCK_DEVELOPMENT_QUERY_KEY]})
      void queryClient.invalidateQueries({
        queryKey: [COST_AVOIDANCE_FOR_RANGE_QUERY_KEY],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [COST_AVOIDANCE_DAILY_QUERY_KEY],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [COST_AVOIDANCE_MONTHLY_QUERY_KEY],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [OPTIMIZED_SCHEDULES_STATS_QUERY_KEY, {plantCode}],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY, {plantCode}],
        refetchType: 'all'
      })
    }
  })

  const acceptOptimizedPlan = useCallback((scheduleId: number) => mutate({scheduleId}), [mutate])

  return {acceptOptimizedPlan, isLoading}
}
